import type { OrgUnitDecision, OrgValidationTree } from "@/api/diagnostic-org-structure";

/**
 * A prévia da propagação do escopo, no front (#1342, F6).
 *
 * Espelho de `services/api/app/services/org_validation_rules.py`. A tela usa isto
 * para avisar, antes de gravar, quantos objetos uma decisão vai afetar. Quem
 * impõe a regra é o servidor: depois do PUT a árvore é relida, e se a prévia
 * divergir da resposta, vale a resposta. Os testes repetem os casos do Python um
 * a um, para a divergência não passar despercebida.
 *
 * A unidade é identificada por `tipo:chave`, a mesma identidade da árvore.
 */

const OUT_OF_SCOPE = "out_of_scope";

/** Como a unidade se liga à árvore: o pai único, ou os pais de um vínculo N:M. */
export interface UnitLinks {
  parent: string | null;
  associated: string[];
}

/** A decisão que vale para a unidade, e se ela veio de cima. */
export interface Effective {
  decision: OrgUnitDecision | null;
  inherited: boolean;
}

export interface DecisionPreview {
  /** Unidades, além da escolhida, cuja decisão efetiva muda. Em ordem. */
  affected: string[];
  /** Unidades que estavam fora explicitamente e voltam a Pendente. Em ordem. */
  resetToPending: string[];
}

function unitId(itemType: string, itemKey: string): string {
  return `${itemType}:${itemKey}`;
}

/** A ordem do Python (`sorted` de tuplas): pelo tipo e depois pela chave. */
function byUnit(a: string, b: string): number {
  const [typeA, keyA] = splitUnit(a);
  const [typeB, keyB] = splitUnit(b);
  if (typeA !== typeB) return typeA < typeB ? -1 : 1;
  if (keyA === keyB) return 0;
  return keyA < keyB ? -1 : 1;
}

function splitUnit(unit: string): [string, string] {
  const at = unit.indexOf(":");
  return [unit.slice(0, at), unit.slice(at + 1)];
}

/** Os vínculos de cada unidade, a partir do `parent_key` e das `associations` do contrato. */
export function orgLinks(tree: OrgValidationTree): Map<string, UnitLinks> {
  const parentLevel = new Map(
    tree.levels.map((level) => [level.level_code, level.parent_level_code ?? null]),
  );
  return new Map(
    tree.nodes.map((node) => {
      const level = parentLevel.get(node.item_type) ?? null;
      return [
        unitId(node.item_type, node.item_key),
        {
          parent: node.parent_key && level ? unitId(level, node.parent_key) : null,
          associated: node.associations.map((ref) => unitId(ref.item_type, ref.item_key)),
        },
      ];
    }),
  );
}

/**
 * A decisão efetiva de cada unidade.
 *
 * Fora do escopo se o pai único estiver fora, ou se a unidade for N:M e **todos**
 * os pais associados estiverem fora. Nos demais casos vale a decisão da própria
 * unidade. Pai que não está nos vínculos (a extração não o trouxe) não propaga.
 */
export function resolveEffective(
  links: ReadonlyMap<string, UnitLinks>,
  own: ReadonlyMap<string, OrgUnitDecision | null>,
): Map<string, Effective> {
  const resolved = new Map<string, Effective>();

  const resolve = (key: string, visiting: ReadonlySet<string>): Effective => {
    const cached = resolved.get(key);
    if (cached) return cached;
    const link = links.get(key) ?? { parent: null, associated: [] };
    let result: Effective = { decision: own.get(key) ?? null, inherited: false };
    if (!visiting.has(key)) {
      const inner = new Set(visiting).add(key);
      if (link.parent !== null && links.has(link.parent)) {
        if (resolve(link.parent, inner).decision === OUT_OF_SCOPE) {
          result = { decision: OUT_OF_SCOPE, inherited: true };
        }
      } else if (link.associated.length > 0) {
        const parents = link.associated
          .filter((parent) => links.has(parent))
          .map((parent) => resolve(parent, inner));
        if (parents.length > 0 && parents.every((parent) => parent.decision === OUT_OF_SCOPE)) {
          result = { decision: OUT_OF_SCOPE, inherited: true };
        }
      }
    }
    resolved.set(key, result);
    return result;
  };

  for (const key of links.keys()) resolve(key, new Set());
  return resolved;
}

/**
 * Os descendentes de pai único que estavam fora e voltam a Pendente quando `key`
 * deixa de estar fora. A unidade N:M fica de fora: a decisão sobre um dos pais não
 * diz nada sobre ela.
 */
export function resetOnRevert(
  links: ReadonlyMap<string, UnitLinks>,
  own: ReadonlyMap<string, OrgUnitDecision | null>,
  key: string,
): string[] {
  const children = new Map<string, string[]>();
  for (const [child, link] of links) {
    if (link.parent === null) continue;
    const list = children.get(link.parent);
    if (list) list.push(child);
    else children.set(link.parent, [child]);
  }

  const found: string[] = [];
  const pending = [...(children.get(key) ?? [])];
  const seen = new Set([key]);
  while (pending.length > 0) {
    const current = pending.pop()!;
    if (seen.has(current)) continue;
    seen.add(current);
    if (own.get(current) === OUT_OF_SCOPE) found.push(current);
    pending.push(...(children.get(current) ?? []));
  }
  return found.sort(byUnit);
}

/**
 * O que gravar `next` em `key` vai mudar na árvore, calculado antes do PUT.
 *
 * Segue a ordem do servidor: se a unidade deixa de estar fora, os descendentes
 * que estavam fora explicitamente voltam a Pendente, e só então a decisão
 * efetiva é recalculada.
 */
export function previewDecision(
  tree: OrgValidationTree,
  key: string,
  next: OrgUnitDecision | null,
): DecisionPreview {
  const links = orgLinks(tree);
  const own = new Map(
    tree.nodes.map((node) => [
      unitId(node.item_type, node.item_key),
      node.validation.decision ?? null,
    ]),
  );
  const before = resolveEffective(links, own);

  const reverting = own.get(key) === OUT_OF_SCOPE && next !== OUT_OF_SCOPE;
  const resetToPending = reverting ? resetOnRevert(links, own, key) : [];
  const after = new Map(own);
  for (const unit of resetToPending) after.set(unit, null);
  after.set(key, next);
  const effective = resolveEffective(links, after);

  const affected = [...links.keys()]
    .filter((unit) => unit !== key)
    .filter((unit) => before.get(unit)?.decision !== effective.get(unit)?.decision)
    .sort(byUnit);
  return { affected, resetToPending };
}
